import { z } from 'zod'

const isServer = (): boolean => typeof window === 'undefined'

/**
 * This module provides a safe interface for interacting with local & session storage,
 * ensuring that data is validated against a provided schema before being used
 *
 * This is crucial for preventing errors on malformed/unexpected data and security vulnerabilities.
 */
export const safeStorage = {
  /**
   * @param key - The key to retrieve from localStorage
   * @param schema - The schema to validate the retrieved data against
   * @param storage - Optional storage object (defaults to localStorage)
   *
   * @return The validated data if it matches the schema, otherwise null
   */
  get<T>(key: string, schema: z.ZodType<T>, storage?: Storage): T | null {
    if (isServer()) return null

    const usedStorage = storage || localStorage

    try {
      const rawValue = usedStorage.getItem(key)
      if (!rawValue) return null

      const parsed = JSON.parse(rawValue)
      const result = schema.parse(parsed)

      return result
    } catch (error) {
      // TODO: log in sentry
      console.error(`Error retrieving key "${key}" from storage:`, error)
      usedStorage.removeItem(key)
      return null
    }
  },

  set<T>(key: string, value: T, schema: z.ZodType<T>, storage?: Storage): void {
    if (isServer()) return

    const usedStorage = storage || localStorage

    try {
      const result = schema.safeParse(value)

      if (!result.success) {
        // TODO: log in sentry
        console.error(`Storage validation failed for key "${key}":`, result.error)
        return
      }

      usedStorage.setItem(key, JSON.stringify(result.data))
    } catch (error) {
      // TODO: log in sentry
      console.error(`Error setting key "${key}" in storage:`, error)
      usedStorage.removeItem(key)
    }
  },
}
